import { convertValidationRules } from '../../../../../hooks/useFormState';
import { BaseValidationContext } from '../../../../../hooks/useFormState.types';
import { useTranslation } from '../../../../../hooks/useTranslation';
import {
  validateEmail,
  validateExistsAndAlphabetCharacters,
  validateFullSSN,
  validateNameField,
  validateNameFieldStrictCasing,
  validatePhoneNumber,
  validateRequiredAndValidCharacters,
  validateStateIdOrDiversLicense,
  isAtLeastEighteenYearsOfAge,
  validateLastFourDigitsCoCustomer,
  validateLastFourDigitsCustomer,
  validateAlphabetCharacters,
  validateITINCustomRule,
} from '../../../../../util/form-validation';
import {
  CoApplicant,
  PersonPrimaryIdType,
} from '../../../../../__generated__/pge-types';
import {
  SSMCoCustomerInfoFormModel,
  SSMCoCustomerVerificationFormModel,
} from '../../../ssm.types';
import { parse, isPast, isBefore, subYears, format } from 'date-fns';
import {
  displayPhoneNumberFormat,
  formatSocialSecurity,
} from '../../../../../util/format';

const { t } = useTranslation();

export const createCoCustomerInfoValidateFunction = () =>
  convertValidationRules<SSMCoCustomerInfoFormModel>(
    (context: BaseValidationContext<SSMCoCustomerInfoFormModel>) => {
      const {
        values: { alternatePhone, useForMobile, mobilePhone },
      } = context;

      return {
        firstName: validateExistsAndAlphabetCharacters.bind(
          null,
          t('FIRST_NAME'),
        ),

        middleName: validateAlphabetCharacters.bind(
          null,
          t('MIDDLE_NAME_OR_INITIAL'),
        ),

        lastName: validateExistsAndAlphabetCharacters.bind(
          null,
          t('LAST_NAME'),
        ),

        email: validateEmail,
        relationship: validateNameField.bind(null, t('RELATIONSHIP')),
        primaryPhone: validatePhoneNumber,
        mobilePhone:
          useForMobile && Number(mobilePhone?.length) > 0
            ? validatePhoneNumber
            : null,
        alternatePhone:
          Number(alternatePhone?.length) > 0 ? validatePhoneNumber : () => null,
      };
    },
  );

interface ValidationMap<TValue> {
  [id: string]: TValue | null;
}

export const idTypeValueValidators: ValidationMap<(value?: any) => any> = {
  [PersonPrimaryIdType.Ssn]: () => validateFullSSN,
  [PersonPrimaryIdType.Dl]: () =>
    validateStateIdOrDiversLicense.bind(
      null,
      `${t('DRIVERS_LICENSE')} / ${t('STATE_ID_CARD')}`,
    ),
  [PersonPrimaryIdType.Matr]: () =>
    validateRequiredAndValidCharacters.bind(null, t('MATRICULA_ID')),
  [PersonPrimaryIdType.Pssprt]: () =>
    validateRequiredAndValidCharacters.bind(null, t('PASSPORT')),
  [PersonPrimaryIdType.Resalien]: () =>
    validateRequiredAndValidCharacters.bind(null, t('RESIDENT_ALIEN_ID')),
  [PersonPrimaryIdType.Visa]: () =>
    validateRequiredAndValidCharacters.bind(null, t('VISA_ID')),
  [PersonPrimaryIdType.Military]: () =>
    validateRequiredAndValidCharacters.bind(null, t('MILITARY_ID')),
  [PersonPrimaryIdType.Ortrib]: () =>
    validateRequiredAndValidCharacters.bind(null, t('OREGON_TRIBAL_ID')),
  [PersonPrimaryIdType.Itin]: () =>
    validateITINCustomRule.bind(null, t('ITIN')),
};
export const idTypeValueLastFourDigitsValidators: ValidationMap<(
  value?: any,
) => any> = {
  [PersonPrimaryIdType.Ssn]: () =>
    validateLastFourDigitsCoCustomer.bind(null, t('SOCIAL_SECURITY_NUMBER')),
  [PersonPrimaryIdType.Dl]: () =>
    validateLastFourDigitsCoCustomer.bind(
      null,
      `${t('DRIVERS_LICENSE')} / ${t('STATE_ID_CARD')}`,
    ),
  [PersonPrimaryIdType.Matr]: () =>
    validateLastFourDigitsCoCustomer.bind(null, t('MATRICULA_ID')),
  [PersonPrimaryIdType.Pssprt]: () =>
    validateLastFourDigitsCoCustomer.bind(null, t('PASSPORT')),
  [PersonPrimaryIdType.Resalien]: () =>
    validateLastFourDigitsCoCustomer.bind(null, t('RESIDENT_ALIEN_ID')),
  [PersonPrimaryIdType.Visa]: () =>
    validateLastFourDigitsCoCustomer.bind(null, t('VISA_ID')),
  [PersonPrimaryIdType.Military]: () =>
    validateLastFourDigitsCoCustomer.bind(null, t('MILITARY_ID')),
  [PersonPrimaryIdType.Ortrib]: () =>
    validateLastFourDigitsCoCustomer.bind(null, t('OREGON_TRIBAL_ID')),
};

export const primaryCustomerFourDigitsValidator: ValidationMap<(
  value?: any,
) => any> = {
  [PersonPrimaryIdType.Ssn]: () =>
    validateLastFourDigitsCustomer.bind(null, t('SOCIAL_SECURITY_NUMBER')),
  [PersonPrimaryIdType.Dl]: () =>
    validateLastFourDigitsCustomer.bind(
      null,
      `${t('DRIVERS_LICENSE')} / ${t('STATE_ID_CARD')}`,
    ),
  [PersonPrimaryIdType.Matr]: () =>
    validateLastFourDigitsCustomer.bind(null, t('MATRICULA_ID')),
  [PersonPrimaryIdType.Pssprt]: () =>
    validateLastFourDigitsCustomer.bind(null, t('PASSPORT')),
  [PersonPrimaryIdType.Resalien]: () =>
    validateLastFourDigitsCustomer.bind(null, t('RESIDENT_ALIEN_ID')),
  [PersonPrimaryIdType.Visa]: () =>
    validateLastFourDigitsCustomer.bind(null, t('VISA_ID')),
  [PersonPrimaryIdType.Military]: () =>
    validateLastFourDigitsCustomer.bind(null, t('MILITARY_ID')),
  [PersonPrimaryIdType.Ortrib]: () =>
    validateLastFourDigitsCustomer.bind(null, t('OREGON_TRIBAL_ID')),
};

export const validateCustomerBirthdate = (dob: string) => {
  const error = t('ADD_CO_CUSTOMER_BIRTDATE_ERROR');

  const BIRTHDAYPATTERN = /\d{2}\/\d{2}\/\d{4}/;

  // Required
  if (!dob || dob === '') {
    return t('DATE_OF_BIRTH_REQUIRED');
  }

  // Fully formatted date
  if (!BIRTHDAYPATTERN.test(dob)) {
    return error;
  }

  const date = parse(dob, 'MM/dd/yyyy', new Date(0));

  // Cannot be in future or 115 years in past
  if (!isPast(date)) {
    return error;
  }

  if (isBefore(date, subYears(new Date(), 115))) {
    return error;
  }

  //Age should not be under 18
  if (!isAtLeastEighteenYearsOfAge(dob)) {
    return error;
  }

  return null;
};

export const createCoCustomerVerifyValidateFunction = () =>
  convertValidationRules<SSMCoCustomerVerificationFormModel>(
    (context: BaseValidationContext<SSMCoCustomerVerificationFormModel>) => {
      const { values } = context;

      return {
        idType: validateNameFieldStrictCasing.bind(null, t('ID_TYPE')),
        idState:
          values.idType === PersonPrimaryIdType.Dl
            ? validateNameField.bind(null, t('STATE'))
            : null,
        idValue: idTypeValueValidators[values.idType]?.(),
        birthdate: validateCustomerBirthdate,
      };
    },
  );

export const createCoCustomerVerifyLastFourDigitsValidateFunction = () =>
  convertValidationRules<SSMCoCustomerVerificationFormModel>(
    (context: BaseValidationContext<SSMCoCustomerVerificationFormModel>) => {
      const { values } = context;

      return {
        idType: null,
        idState: null,
        idValue: idTypeValueLastFourDigitsValidators[values.idType]?.(),
        birthdate: null,
      };
    },
  );

type Props = {
  customerContactInfo: SSMCoCustomerInfoFormModel &
    SSMCoCustomerVerificationFormModel;
  accountNumber: string;
  mainPerId: string | undefined;
  personId: string | null | undefined;
};

const getFormatedPhoneNumber = (phone: string) => {
  return displayPhoneNumberFormat(phone?.replace(/[^0-9]/g, ''));
};

export const createCoCustomerPayload = ({
  customerContactInfo,
  accountNumber,
  mainPerId,
  personId,
}: Props) => {
  const payload: CoApplicant = {
    lastName: customerContactInfo.lastName,
    firstName: customerContactInfo.firstName,
    middleName: customerContactInfo.middleName,
    acctId: accountNumber,
    mainPerId,
    perId: personId,
    additionalInfo: {
      primaryPhone: getFormatedPhoneNumber(customerContactInfo.primaryPhone),
      dateOfBirth: customerContactInfo.birthdate
        ? format(
            parse(customerContactInfo.birthdate, 'dd/mm/yyyy', new Date()),
            'yyyy-dd-mm',
          )
        : null,
      emailAddress: customerContactInfo.email,
      primaryPhoneIsMobile: customerContactInfo.useForMobile,
      mobPhone: getFormatedPhoneNumber(
        customerContactInfo.useForMobile
          ? customerContactInfo.primaryPhone
          : customerContactInfo.mobilePhone,
      ),
      idInfo: {
        idType: customerContactInfo.idType,
        idValue: ((customerContactInfo.idType ===
          PersonPrimaryIdType.Ssn ||
          customerContactInfo.idType ===
            PersonPrimaryIdType.Itin) &&
        customerContactInfo.idValue.length !== 4
          ? formatSocialSecurity(customerContactInfo.idValue)
          : customerContactInfo.idValue
        )?.trim(),
        state: customerContactInfo.idState,
      },
    },
  };
  if (customerContactInfo.alternatePhone) {
    payload.additionalInfo.altPhone = getFormatedPhoneNumber(
      customerContactInfo.alternatePhone,
    );
  }
  return payload;
};
